import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { LuFlame, LuPhone, LuMessageSquare, LuChevronRight } from "react-icons/lu";
import { getPrioritizedLeads } from "../../Services/engagementService";

const tierStyle = {
  Hot: { bg: "bg-red-50", text: "text-red-600", ring: "ring-red-200", dot: "#EF4444" },
  Warm: { bg: "bg-amber-50", text: "text-amber-600", ring: "ring-amber-200", dot: "#F59E0B" },
  Cold: { bg: "bg-slate-50", text: "text-slate-500", ring: "ring-slate-200", dot: "#94A3B8" },
};

const waLink = (m, t) =>
  `https://wa.me/${String(m || "").replace(/\D/g, "")}?text=${encodeURIComponent(t || "")}`;

const PriorityLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const navigate = useNavigate();

  useEffect(() => {
    getPrioritizedLeads()
      .then((d) => setLeads(d || []))
      .catch(() => setLeads([]))
      .finally(() => setLoading(false));
  }, []);

  const counts = leads.reduce((a, l) => {
    a[l.tier] = (a[l.tier] || 0) + 1;
    return a;
  }, {});
  const shown = filter === "All" ? leads : leads.filter((l) => l.tier === filter);

  return (
    <div className="page-container p-6">
      <div className="flex items-center gap-2 mb-1">
        <LuFlame className="text-red-500" size={26} />
        <h1 className="text-2xl font-bold text-gray-800">Priority Leads</h1>
      </div>
      <p className="text-gray-500 mb-6">Work the hottest leads first — ranked by freshness, intent, and value.</p>

      <div className="flex gap-2 mb-5 flex-wrap">
        {["All", "Hot", "Warm", "Cold"].map((t) => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              filter === t ? "bg-[#425F80] text-white" : "bg-gray-100 text-gray-600"
            }`}
          >
            {t} {t !== "All" && counts[t] ? `· ${counts[t]}` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="text-gray-400 py-10 text-center">No leads to show.</p>
      ) : (
        <div className="space-y-2">
          {shown.map((l) => {
            const s = tierStyle[l.tier] || tierStyle.Cold;
            return (
              <div
                key={l.lpr_id}
                className="flex items-center gap-4 bg-white rounded-xl border border-gray-100 px-4 py-3"
              >
                <div className={`w-12 h-12 rounded-full ${s.bg} ring-1 ${s.ring} flex flex-col items-center justify-center shrink-0`}>
                  <span className={`text-sm font-bold ${s.text}`}>{l.score}</span>
                </div>
                <button className="flex-1 text-left" onClick={() => navigate(`/leads/details/${l.lpr_id}`)}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800">{l.name || "Lead"}</span>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.bg} ${s.text}`}>
                      {l.tier}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">
                    {l.product_name || "—"} · {moment(l.created_at).fromNow()}
                  </p>
                </button>
                <div className="flex items-center gap-1">
                  <a
                    href={waLink(l.contact_number, `Hi ${l.name || ""}, `)}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 rounded-full bg-[#25D366]/10 text-[#128C7E]"
                    title="WhatsApp"
                  >
                    <LuMessageSquare size={16} />
                  </a>
                  <a
                    href={`tel:${l.contact_number}`}
                    className="p-2 rounded-full bg-[#425F80]/10 text-[#425F80]"
                    title="Call"
                  >
                    <LuPhone size={16} />
                  </a>
                  <LuChevronRight className="text-gray-300" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PriorityLeads;
